import { motion } from 'framer-motion';
import { 
  Users, 
  Target, 
  Award, 
  Zap, 
  Globe, 
  Heart,
  TrendingUp,
  Shield,
  Lightbulb,
  CheckCircle,
  FileText,
  Linkedin
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

const AboutUsContent: React.FC = () => {
  const { t } = useTranslation();
  const stats = [
    { labelKey: 'views.about.resumesOptimized', value: '10,000+', icon: FileText },
    { labelKey: 'views.about.successRate', value: '95%', icon: TrendingUp },
    { labelKey: 'views.about.countriesServed', value: '50+', icon: Globe },
    { labelKey: 'views.about.avgScoreImprovement', value: '85%', icon: Award }
  ];

  const values = [
    { icon: Target, titleKey: 'views.about.value1Title', descKey: 'views.about.value1Desc' },
    { icon: Lightbulb, titleKey: 'views.about.value2Title', descKey: 'views.about.value2Desc' },
    { icon: Heart, titleKey: 'views.about.value3Title', descKey: 'views.about.value3Desc' },
    { icon: Shield, titleKey: 'views.about.value4Title', descKey: 'views.about.value4Desc' }
  ];

  return (
    <div className="space-y-16">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center">
            <Users className="w-10 h-10 text-[var(--vscode-primary)]" />
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold vscode-text-primary mb-6">{t('views.about.title')}</h1>
        <p className="text-xl vscode-text-secondary max-w-3xl mx-auto leading-relaxed">
          {t('views.about.subtitle')}
        </p>
      </div>

      {/* Our Story */}
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="vscode-bg-elevated rounded-2xl p-8 border vscode-border"
        >
          <h2 className="text-3xl font-bold vscode-text-primary mb-6 flex items-center">
            <Lightbulb className="w-8 h-8 text-[var(--vscode-primary)] mr-3" />
            {t('views.about.ourStory')}
          </h2>
          <div className="space-y-6 vscode-text-secondary leading-relaxed">
            <p>{t('views.about.ourStoryPara1')}</p>
            <p>{t('views.about.ourStoryPara2')}</p>
            <p>{t('views.about.ourStoryPara3')}</p>
          </div>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.labelKey}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="vscode-bg-elevated rounded-xl p-6 border vscode-border text-center"
          >
            <div className="w-12 h-12 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <stat.icon className="w-6 h-6 text-[var(--vscode-primary)]" />
            </div>
            <div className="text-2xl font-bold vscode-text-primary mb-2">{stat.value}</div>
            <div className="vscode-text-tertiary text-sm">{t(stat.labelKey)}</div>
          </motion.div>
        ))}
      </div>

      {/* Our Values */}
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold vscode-text-primary mb-4">{t('views.about.ourValues')}</h2>
          <p className="text-xl vscode-text-secondary">
            {t('views.about.ourValuesSubline')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.titleKey}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="vscode-bg-elevated rounded-xl p-6 border vscode-border"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-6 h-6 text-[var(--vscode-primary)]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold vscode-text-primary mb-2">{t(value.titleKey)}</h3>
                  <p className="vscode-text-secondary leading-relaxed">{t(value.descKey)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Technology */}
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="vscode-bg-elevated rounded-2xl p-8 border vscode-border"
        >
          <h2 className="text-3xl font-bold vscode-text-primary mb-6 flex items-center">
            <Zap className="w-8 h-8 text-[var(--vscode-primary)] mr-3" />
            {t('views.about.ourTechnology')}
          </h2>
          <div className="space-y-6 vscode-text-secondary leading-relaxed">
            <p>{t('views.about.techPara1')}</p>
            <p>{t('views.about.techPara2')}</p>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold vscode-text-primary">{t('views.about.keyFeaturesHeading')}</h3>
                <ul className="space-y-2 vscode-text-secondary">
                  {['keyFeature1', 'keyFeature2', 'keyFeature3', 'keyFeature4', 'keyFeature5'].map((key) => (
                    <li key={key} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-[var(--vscode-success)] mr-2 flex-shrink-0" />
                      <span>{t(`views.about.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold vscode-text-primary">{t('views.about.technologiesHeading')}</h3>
                <ul className="space-y-2">
                  {['tech1', 'tech2', 'tech3', 'tech4', 'tech5'].map((key) => (
                    <li key={key} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-[var(--vscode-primary)] mr-2 flex-shrink-0" />
                      <span>{t(`views.about.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Team */}
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold vscode-text-primary mb-4">Meet the Team</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">

          {/* Dharmendra Singh */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="vscode-bg-elevated rounded-2xl border vscode-border overflow-hidden flex flex-col"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-[var(--vscode-primary)] to-purple-400" />
            <div className="p-6 flex flex-col items-center text-center gap-4 flex-1">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-2 border-[var(--vscode-primary)]/25 shadow-lg flex-shrink-0">
                <img
                  src="/team/dharmendra-singh.png"
                  alt="Dharmendra Singh"
                  width={128}
                  height={128}
                  className="w-full h-full object-cover object-[50%_15%]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold vscode-text-primary mb-4">Dharmendra Singh</h3>
                <p className="text-sm vscode-text-secondary leading-relaxed">
                  Building GoGrabJob to give every job-seeker the same unfair advantage that great resume coaches give the lucky few. Obsessed with helping people land the roles they deserve.
                </p>
              </div>
              <a
                href="https://www.linkedin.com/in/dharmendra-s-77297014/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] hover:bg-[#004182] text-white transition-colors text-sm font-semibold w-full justify-center"
              >
                <Linkedin className="w-4 h-4 flex-shrink-0" />
                LinkedIn
              </a>
            </div>
          </motion.div>

          {/* Ramesh Behera — Team */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="vscode-bg-elevated rounded-2xl border vscode-border overflow-hidden flex flex-col"
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-purple-400 to-pink-400" />
            <div className="p-6 flex flex-col items-center text-center gap-4 flex-1">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-2 border-purple-400/25 shadow-lg flex-shrink-0">
                <img
                  src="/team/ramesh-behera.png"
                  alt="Ramesh Behera"
                  width={128}
                  height={128}
                  className="w-full h-full object-cover object-[50%_15%]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold vscode-text-primary mb-4">Ramesh Behera</h3>
                <p className="text-sm vscode-text-secondary leading-relaxed">
                  A core part of the GoGrabJob team, helping build tools that make a real difference in the lives of job-seekers across India and beyond.
                </p>
              </div>
              <a
                href="https://www.linkedin.com/in/ramesh-behera-97386616/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] hover:bg-[#004182] text-white transition-colors text-sm font-semibold w-full justify-center"
              >
                <Linkedin className="w-4 h-4 flex-shrink-0" />
                LinkedIn
              </a>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Call to Action */}
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[var(--vscode-primary)]/10 rounded-2xl p-8 border border-[var(--vscode-primary)]/20"
        >
          <h2 className="text-3xl font-bold vscode-text-primary mb-4">{t('views.about.ctaTitle')}</h2>
          <p className="text-xl vscode-text-secondary mb-8">
            {t('views.about.ctaSubline')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/signup"
              className="inline-flex items-center justify-center px-8 py-3 bg-[var(--vscode-primary)] hover:opacity-90 text-[var(--vscode-primary-text)] font-semibold rounded-lg transition-colors"
            >
              {t('views.about.getStartedFree')}
            </a>
            <a
              href="/faqs"
              className="inline-flex items-center justify-center px-8 py-3 border vscode-border vscode-text-secondary font-semibold rounded-lg hover:vscode-bg-hover transition-colors"
            >
              {t('views.about.learnMore')}
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUsContent;
